import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState, useMemo, useRef, useContext } from "react";
import { WorkflowsContext } from "@/context/WorkflowsContext";

import { Link } from "react-router-dom";
import useTeams from "@/hooks/useTeams";
import useChatWindows from "@/hooks/useChatWindows";
import useSubscriptions from "@/hooks/useSubscriptions";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import DeploymentTabs from "./DeploymentTabs";
import useRules from "@/hooks/useRules";
import { Rule } from "@/pages/Messaging/Rules/types";
import { ChatWindowValue } from "@/pages/Messaging/Rules/ValueForms/ChatWindowRuleForm";
import DeploymentSelector from "./DeploymentSelector";
import AddDeploymentModal from "./AddDeploymentModal";
import {
  SecondaryBlueButton, PrimaryBlueButton, FormValidator, Severity, Toggle
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import AuthService from "@/services/AuthService";
import useToastAlert from "@/hooks/useToastAlert";
import useAsync from "@/hooks/useAsync";
import rawEmbedScript from "@/pages/Messaging/WebMessenger/components/DeployModal/EmbedScript.html?raw";
import DeploymentSecurityGuide from "./DeploymentSecurityGuide";
import PageWidgets from "@/pages/Messaging/PageWidgets";

import FormGroup from "@/components/FormGroup";
import Select from "@/components/Select";

export interface Deployment {
  id: number;
  deploymentId: string;
  displayName: string;
  teamId: number;
  siteId: number;
}

export default function Deployments() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const features = useAdminFeatures();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("General");
  const [selectedTeam, setSelectedTeam] = useState<number>(0);
  const [chatAvailable, setChatAvailable] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedChatWindow, setSelectedChatWindow] = useState<string>("");
  const [hasExistingRule, setHasExistingRule] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // Copilot FAB removed — MainLayout provides the global Copilot on every page
  // const [showCopilot, setShowCopilot] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [customFieldNames, setCustomFieldNames] = useState<string[]>([]);
  const [channelParams, setChannelParams] = useState<string>("");
  const toastAlert = useToastAlert();

  const { teams, getTeamNameById } = useTeams();
  const { rules, add: addRule, update: updateRule } = useRules();
  const { chatWindows } = useChatWindows();
  const { workflows, fetch: refreshWorkflows } = useContext(WorkflowsContext);
  const seededRef = useRef(false);

  const maxDeployments = features.maxDeployments ?? 1;
  const activeDeploymentCount = Array.isArray(deployments) ? deployments.length : 0;
  // 0 = unlimited (server convention: MaxDeployments=0 means no cap). Never block when 0.
  const canCreateDeployment = maxDeployments === 0 || activeDeploymentCount < maxDeployments;
  const showSelector =
    maxDeployments > 1 ||
    (Array.isArray(deployments) && deployments.length > 1);

  const fetchDeployments = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("Deployment");
      const data = await resp.json();
      const list = Array.isArray(data) ? data : [];
      setDeployments(list);
      // If we have a current deployment, keep it selected; otherwise pick first
      if (list.length > 0) {
        setDeployment((prev) => {
          const found = prev ? list.find((d: Deployment) => d.id === prev.id) : null;
          const active = found ?? list[0];
          setSelectedTeam(active.teamId ?? 0);
          return active;
        });
      }
    } catch {
      setDeployments([]);
    }
    setLoading(false);
  }, [api.messaging]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  // Auto-seed a sample workflow for brand-new accounts that have none.
  // Runs once after deployments are loaded and only when workflows is empty.
  useEffect(() => {
    if (seededRef.current) return;
    if (loading) return;
    if (workflows.length > 0) return;

    seededRef.current = true;
    api.admin
      .post("SiteSetup/seed-sample-workflow", {})
      .then((res) => res.json())
      .then((data) => {
        if (data.created) {
          refreshWorkflows();
          toastAlert.displayToast(
            Severity.Info,
            "A sample welcome workflow was created so you can test chat right away. Find it in Workflows whenever you want to customize or delete it."
          );
        }
      })
      .catch((err) => {
        console.error("SiteSetup: failed to seed sample workflow", err);
      });
  }, [loading, workflows.length, api.admin, refreshWorkflows, toastAlert]);

  // Load custom field names for the visitor data code example
  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.messaging.get("ContactFieldDefinitions/list");
        if (res.ok) {
          const data = await res.json();
          const defs = Array.isArray(data)
            ? data
            : Array.isArray(data?.definitions)
              ? data.definitions
              : [];
          const names: string[] = defs
            .filter((d: { name?: string }) => d.name)
            .map((d: { name: string }) => d.name);
          setCustomFieldNames(names);
        }
      } catch {
        // not critical
      }
    }
    loadFields();
  }, [api.messaging]);

  // Fetch configured channels so the test playground can show real test numbers/links
  useEffect(() => {
    async function loadChannels() {
      try {
        const [smsRes, fbRes, igRes, waRes, emailRes] = await Promise.allSettled([
          api.messaging.get("Sms/Integrations"),
          api.messaging.get("Facebook/Integrations"),
          api.messaging.get("Instagram/Integrations"),
          api.messaging.get("WhatsApp/Integrations"),
          api.messaging.get("GmailOAuth/list"),
        ]);

        const params = new URLSearchParams();

        if (smsRes.status === "fulfilled" && smsRes.value.ok) {
          const data = await smsRes.value.json();
          const nums = (Array.isArray(data) ? data : [])
            .map((x: any) => x.phoneNumber)
            .filter(Boolean);
          if (nums.length) params.set("sms", nums.join(","));
        }
        if (fbRes.status === "fulfilled" && fbRes.value.ok) {
          const data = await fbRes.value.json();
          const pages = (Array.isArray(data) ? data : [])
            .map((x: any) => x.pageId && x.pageName ? `${encodeURIComponent(x.pageName)}|${x.pageId}` : null)
            .filter(Boolean);
          if (pages.length) params.set("fb", pages.join(","));
        }
        if (igRes.status === "fulfilled" && igRes.value.ok) {
          const data = await igRes.value.json();
          const users = (Array.isArray(data) ? data : [])
            .map((x: any) => x.username)
            .filter(Boolean);
          if (users.length) params.set("ig", users.join(","));
        }
        if (waRes.status === "fulfilled" && waRes.value.ok) {
          const data = await waRes.value.json();
          // We only store PhoneId (Meta internal), not the actual number — just flag as configured
          const count = (Array.isArray(data) ? data : []).filter((x: any) => !x.deleted).length;
          if (count > 0) params.set("wa", "1");
        }
        if (emailRes.status === "fulfilled" && emailRes.value.ok) {
          const data = await emailRes.value.json();
          const addrs = (Array.isArray(data) ? data : [])
            .filter((x: any) => x.isEnabled !== false)
            .map((x: any) => x.mailboxEmail)
            .filter(Boolean);
          if (addrs.length) params.set("em", addrs.join(","));
        }

        setChannelParams(params.toString());
      } catch {
        // non-critical — preview still works without channel info
      }
    }
    loadChannels();
  }, [api.messaging]);

  // Check if deployment already has a chat window rule
  useEffect(() => {
    const existingRule = rules.find(r =>
      r.trigger === "determine-chat-window"
      // TODO: Add deployment context filter when rules support per-deployment scoping (Phase 2)
    );

    if (existingRule) {
      setHasExistingRule(true);
      setSelectedChatWindow((existingRule.value as ChatWindowValue)?.chatWindow ?? "");
    }
  }, [rules]);

  const embedScript = useMemo(() => {
    if (!deployment || loading) {
      return "";
    }
    // Strip HTML comments (CSP usage notes) — customers see clean embed code
    return rawEmbedScript
      .replace(/<!--[\s\S]*?-->\n?/g, "")
      .replace("{{ siteId }}", AuthService.profile.siteId.toString())
      .replace("{{ deploymentId }}", deployment.deploymentId?.toString() ?? "")
      .replace(
        "{{ scriptSrc }}",
        `${import.meta.env.VITE_MESSAGING_CLIENT_URL!}/messenger/embed.js`
      )
      .trim();
  }, [deployment, loading]);

  const linkUrl = useMemo(() => {
    if (!deployment || loading) {
      return "";
    }

    const src = encodeURIComponent(`${import.meta.env.VITE_MESSAGING_CLIENT_URL!}/messenger/embed.js`);
    const name = encodeURIComponent(deployment.displayName || deployment.deploymentId);
    const wf = workflows.length > 0 ? 1 : 0;
    const ht = deployment.teamId ? 1 : 0;
    const base = `${window.location.origin}/embed-preview.html?siteId=${AuthService.profile.siteId.toString()}&deploymentId=${deployment.deploymentId}&name=${name}&src=${src}&wf=${wf}&ht=${ht}`;
    return channelParams ? `${base}&${channelParams}` : base;
  }, [deployment, loading, channelParams, workflows]);

  function handleSelectDeployment(id: number) {
    const found = deployments.find((d) => d.id === id);
    if (found) {
      setDeployment(found);
      setSelectedTeam(found.teamId ?? 0);
      setIsRenaming(false);
      setShowDeleteConfirm(false);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!deployment) return;
    const { name, value } = e.target;
    setDeployment({ ...deployment, [name]: value });
  };

  const handleTeamChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (!deployment) return;
    setSelectedTeam(Number(event.target.value));
    setDeployment({ ...deployment, teamId: Number(event.target.value) });
  };

  const handleChatWindowChange = async (chatWindowName: string) => {
    setSelectedChatWindow(chatWindowName);

    if (!hasExistingRule) {
      const newRule: Rule = {
        trigger: "determine-chat-window",
        priority: 1,
        expression: [],
        value: {
          chatWindow: chatWindowName
        } as ChatWindowValue
      };

      try {
        await addRule(newRule);
        setHasExistingRule(true);
      } catch (error) {
        console.error("Failed to create chat window rule:", error);
      }
    } else {
      const existingRule = rules.find(r => r.trigger === "determine-chat-window");
      if (existingRule) {
        const updatedRule = {
          ...existingRule,
          value: { chatWindow: chatWindowName } as ChatWindowValue
        };

        try {
          await updateRule(updatedRule);
        } catch (error) {
          console.error("Failed to update chat window rule:", error);
        }
      }
    }
  };

  async function handleSubmit() {
    if (!deployment) return;

    setErrors({});
    const newErrors: Record<string, string> = {};

    if (
      deployment.deploymentId === "" ||
      /^\s*$/.test(deployment.deploymentId)
    ) {
      newErrors["DeploymentId"] = "Deployment Id required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) {
      return;
    }

    const response = await api.messaging.put(
      `Deployment/${deployment.id}`,
      deployment
    );

    if (response.ok) {
      toastAlert.displayToast(
        Severity.Success,
        "Deployment updated successfully"
      );
      // Refresh the list to pick up any name changes
      fetchDeployments();
    }

    if (response.status === 400) {
      const errorResponse = await response.json();
      if (typeof errorResponse.errors === "object") {
        setErrors(errorResponse.errors);
      }
      toastAlert.displayToast(Severity.Error, errorResponse);
    }

    if (response.status === 401) {
      toastAlert.displayToast(
        Severity.Error,
        "You do not have permission to edit this deployment"
      );
    }
  }

  async function handleRename() {
    if (!deployment || !renameValue.trim()) return;

    const updated = { ...deployment, displayName: renameValue.trim() };
    const response = await api.messaging.put(
      `Deployment/${deployment.id}`,
      updated
    );

    if (response.ok) {
      setDeployment(updated);
      setIsRenaming(false);
      toastAlert.displayToast(Severity.Success, "Deployment renamed");
      fetchDeployments();
    } else {
      toastAlert.displayToast(Severity.Error, "Failed to rename deployment");
    }
  }

  async function handleDelete() {
    if (!deployment) return;
    if (deployments.length <= 1) {
      toastAlert.displayToast(Severity.Error, "Cannot delete the last deployment");
      setShowDeleteConfirm(false);
      return;
    }

    try {
      const response = await api.messaging.delete(
        `Deployment/${deployment.id}`
      );

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, "Deployment deleted");
        setShowDeleteConfirm(false);
        fetchDeployments();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to delete deployment");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to delete deployment");
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col">
      <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Deployment</h1>
          <p className="text-xs text-slate-400 mt-0.5">Configure and manage your chat widget deployments</p>
        </div>
      </div>

      {/* Chat Preview — pinned at top so it's always easy to find */}
      {deployment && linkUrl && (
        <div className="max-w-[750px] w-full self-center mb-4 mt-4 flex items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-xl px-5 py-3">
          <div>
            <div className="font-semibold text-sm text-slate-800">
              <i className="fa-solid fa-eye mr-1.5" />
              Chat Preview
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              See exactly how your chat widget looks to visitors
            </div>
          </div>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
            onClick={() => window.open(linkUrl, "_blank")}
          >
            Launch Preview
          </button>
        </div>
      )}

      <DeploymentTabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      {/* Deployment Selector — shown when multi-deployment enabled or >1 deployments */}
      {showSelector && (
        <DeploymentSelector
          deployments={deployments}
          selectedDeploymentId={deployment?.id ?? null}
          onSelect={handleSelectDeployment}
          onAddNew={() => setShowAddModal(true)}
          showAddButton={canCreateDeployment}
        />
      )}

      {/* Deployment usage / limit banner — shown when multiple deployments are possible */}
      {showSelector && (
        <div className="max-w-[750px] w-full self-center mb-2">
          <div className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-xs ${
            canCreateDeployment
              ? "bg-slate-50 border-slate-200 text-slate-600"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}>
            <span>
              <strong>{activeDeploymentCount}</strong> of{" "}
              <strong>{maxDeployments === 0 || maxDeployments >= 2147483647 ? "unlimited" : maxDeployments}</strong>{" "}
              deployments used
            </span>
            {!canCreateDeployment && (
              <Link to="/billing" className="text-amber-700 underline font-medium hover:text-amber-900">
                Upgrade for more
              </Link>
            )}
          </div>
        </div>
      )}

      {/* What are deployments? info panel */}
      <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden max-w-[750px] w-full self-center">
        <button
          type="button"
          onClick={() => setShowInfo(!showInfo)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700 text-left transition-colors"
          aria-expanded={showInfo}
        >
          <span>ℹ️ What is a deployment?</span>
          <span className="text-slate-400 text-base leading-none">{showInfo ? "▲" : "▼"}</span>
        </button>
        {showInfo && (
          <div className="px-4 py-4 text-sm text-slate-600 bg-white border-t border-slate-200 space-y-4">

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <strong>Most customers need only one deployment.</strong> One embed script
              on your site + routing rules = full control over which team handles which
              visitors, what surveys they see, and what language the bot speaks. You
              don&apos;t need multiple deployments for that.
            </div>

            <p>
              A <strong>deployment</strong> is a separate embed script with its own
              deployment ID. Each deployment can have a different default team and
              default surveys. All deployments share the same account, agents,
              workflows, and rules.
            </p>

            {/* Why rules usually beat multiple deployments */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 space-y-2">
              <div className="font-semibold text-sm">Why one deployment + rules is usually enough</div>
              <ul className="space-y-1 list-disc list-inside text-blue-700">
                <li>
                  <strong>Route to Team</strong> rules can send visitors to different
                  teams based on URL, language, country, time of day, visitor data, or
                  any combination — all without changing the embed script.
                </li>
                <li>
                  <strong>Determine Chat Window</strong> rules can change the widget&apos;s
                  appearance per page or visitor segment.
                </li>
                <li>
                  <strong>Surveys</strong> can be assigned per rule, so different pages or
                  visitor segments see different pre-chat, post-chat, or unavailable surveys.
                </li>
                <li>
                  All of this works with <em>one embed script</em> — you configure
                  everything here in the admin, and your developers never need to
                  touch the code again.
                </li>
              </ul>
            </div>

            {/* When you actually would use multiple deployments */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                When multiple deployments make sense (rare)
              </p>
              <p className="text-xs text-slate-500 mb-3">
                Multiple deployments are for situations where you need completely
                isolated chat experiences — each with its own embed script, its own
                default team, and its own default surveys. Think of it as a
                &quot;virtual site within your site.&quot; Note: chat window designs and surveys
                can already be customized per team using rules, so a second deployment
                is rarely needed just for branding differences.
              </p>
              <div className="space-y-2">
                <div className="bg-slate-50 rounded border border-slate-200 px-3 py-2">
                  <p className="text-xs font-medium text-slate-700">Separate brands on one Velaro account</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Your company runs acme.com and globex.com as distinct brands on
                    a single Velaro account. Each brand gets its own embed script so
                    conversations, defaults, and reporting are cleanly separated — even
                    though agents handle chats from both.
                  </p>
                </div>
                <div className="bg-slate-50 rounded border border-slate-200 px-3 py-2">
                  <p className="text-xs font-medium text-slate-700">White-labeling / partner portals</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    You provide chat on behalf of partners or clients. Each partner gets
                    its own deployment with completely separate defaults, while your team
                    manages everything from one account.
                  </p>
                </div>
                <div className="bg-slate-50 rounded border border-slate-200 px-3 py-2">
                  <p className="text-xs font-medium text-slate-700">Web app + standalone kiosk or mobile app</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    You embed chat on your website AND inside a separate native app or
                    physical kiosk. Separate deployments let each surface have its own
                    identity and defaults without one affecting the other.
                  </p>
                </div>
              </div>
            </div>

            {/* Why NOT to use multiple deployments */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
              <div className="font-semibold">You do NOT need multiple deployments if you just want to:</div>
              <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                <li>Route different pages to different teams &rarr; use a <strong>Route to Team</strong> rule with a URL condition</li>
                <li>Show different surveys on different pages &rarr; use a <strong>Determine Survey</strong> rule</li>
                <li>Change the widget look per page &rarr; use a <strong>Determine Chat Window</strong> rule</li>
                <li>Route by language or country &rarr; these are automatic rule conditions</li>
              </ul>
              <p className="text-amber-600 mt-1">
                All of the above work with your single default deployment — no extra embed scripts needed.
              </p>
            </div>

          </div>
        )}
      </div>

      <div className="bg-white p-6 border-[1px] rounded-2xl shadow-sm max-w-[750px] w-full self-center">
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="font-medium text-md pb-1">
                {deployment?.displayName || "Deployment Settings"}
              </div>
              {/* Rename / Delete actions — only in multi-deployment mode */}
              {showSelector && (
                <div className="flex items-center gap-2 pb-1">
                  {!isRenaming ? (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={() => {
                        setRenameValue(deployment?.displayName ?? "");
                        setIsRenaming(true);
                      }}
                      aria-label="Rename deployment"
                    >
                      Rename
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        className="input text-sm px-2 py-1 w-40"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename();
                          if (e.key === "Escape") setIsRenaming(false);
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="text-xs text-green-600 hover:underline"
                        onClick={handleRename}
                        aria-label="Save rename"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:underline"
                        onClick={() => setIsRenaming(false)}
                        aria-label="Cancel rename"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {deployments.length > 1 && !showDeleteConfirm && (
                    <button
                      type="button"
                      className="text-xs text-red-500 hover:text-red-700 hover:underline"
                      onClick={() => setShowDeleteConfirm(true)}
                      aria-label="Delete deployment"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-slate-500 text-xs">
                Site ID: {AuthService.profile.siteId}
              </div>
              <SecondaryBlueButton
                label="Live Preview"
                onClick={() => window.open(linkUrl, "_blank")}
                disabled={!linkUrl}
              />
            </div>
          </div>

          {/* Delete confirmation */}
          {showDeleteConfirm && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-sm text-red-700 mb-2">
                Delete &quot;{deployment?.displayName}&quot;? This cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={handleDelete}
                  aria-label="Confirm delete"
                >
                  Yes, Delete
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50"
                  onClick={() => setShowDeleteConfirm(false)}
                  aria-label="Cancel delete"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="text-slate-600 text-sm pb-2">
            Apply your deployment configurations
          </div>
          <hr className="mt-2 mb-4 border-gray-200" />
        </div>

        {selectedTab === "General" && (
          <div>
            {/* Team */}
            <div className="w-full mb-6">
              <div className="mb-2">
                <label className="block text-sm font-normal text-black mb-1">
                  Team
                </label>
                <div className="text-sm text-slate-600 mb-2">
                  Select the team that will manage this deployment.
                </div>
              </div>
              <Select
                options={
                  Array.isArray(teams)
                    ? teams.reduce((acc, team) => {
                        acc[team.id.toString()] = team.name;
                        return acc;
                      }, {} as Record<string, string>)
                    : {}
                }
                value={selectedTeam.toString()}
                onChange={(value) =>
                  handleTeamChange({
                    target: { value }
                  } as React.ChangeEvent<HTMLSelectElement>)
                }
              />
            </div>

            {/* Chat Window Picker */}
            <div className="w-full mb-6">
              <div className="mb-2">
                <label className="block text-sm font-normal text-black mb-1">
                  Chat Window
                </label>
              </div>
              <div className="text-sm text-slate-600 mb-2">
                Select which chat window to use for this deployment.
              </div>

              <Select
                options={chatWindows?.reduce((acc, window) => {
                  acc[window.name] = window.name;
                  return acc;
                }, {} as Record<string, string>) || {}}
                value={selectedChatWindow}
                onChange={(value) => handleChatWindowChange(value)}
              />

              <div className="mt-2">
                <a
                  href="/chat_window"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs cursor-pointer"
                >
                  Create new chat window
                </a>
              </div>
            </div>

            {/* Chat Availability */}
            <div className="w-full mb-6">
              <div className="flex items-center gap-3 mb-2">
                <label className="block text-sm font-normal text-black">
                  Chat Availability
                </label>
                <Toggle
                  toggled={chatAvailable}
                  changeToggle={() => setChatAvailable(!chatAvailable)}
                />
              </div>
              <div className="text-sm text-slate-600 mb-2">
                By default, chat is available 24/7. Switch off to set your
                custom hours.
              </div>
            </div>

            {/* Save Button */}
            <div className="py-4 flex mt-4">
              <div className="ml-auto flex">
                <PrimaryBlueButton
                  label="Save Changes"
                  onClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        )}
        {selectedTab === "Deployment Code" && (
          <DeploymentCodeTab
            embedScript={embedScript}
            customFieldNames={customFieldNames}
          />
        )}
        {selectedTab === "Page Widgets" && (
          <PageWidgets />
        )}
      </div>

      {/* Add Deployment Modal */}
      {showAddModal && (
        <AddDeploymentModal
          onClose={() => setShowAddModal(false)}
          onDeploymentAdded={() => {
            setShowAddModal(false);
            fetchDeployments();
          }}
        />
      )}

      {/* Copilot FAB removed — MainLayout provides the global Copilot on every page */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deployment Code Tab  (v2 — comprehensive reference)
// ---------------------------------------------------------------------------

interface DeploymentCodeTabProps {
  embedScript: string;
  customFieldNames: string[];
  linkUrl?: string;
}

// Reusable notation helpers (doc-notation-standards.md)
function VarPill({ name }: { name: string }) {
  return (
    <code className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-mono text-xs">
      {`<<${name}>>`}
    </code>
  );
}
function ValPill({ value }: { value: string }) {
  return (
    <code className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded font-mono text-xs">
      &quot;{value}&quot;
    </code>
  );
}
function SysPill({ children }: { children: React.ReactNode }) {
  return (
    <code className="text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded font-mono text-xs break-words">
      {children}
    </code>
  );
}

function CopyBlock({ text, id, copied, onCopy }: { text: string; id: string; copied: string | null; onCopy: (t: string, k: string) => void }) {
  return (
    <div className="relative">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed">
        {text}
      </pre>
      <button
        type="button"
        className="absolute top-2 right-2 text-xs bg-white/10 border border-white/20 text-white rounded px-2 py-1 hover:bg-white/20 transition-colors"
        onClick={() => onCopy(text, id)}
      >
        {copied === id ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function DeploymentCodeTab({ embedScript, customFieldNames }: DeploymentCodeTabProps) {
  const [copied, setCopied] = useState<string | null>(null);

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  }

  const visitorSnippet = buildVisitorSnippet(customFieldNames);

  return (
    <div className="space-y-8">

      {/* ── 1. Embed script ── */}
      <section>
        <div className="font-medium text-sm text-slate-800 mb-1">Embed Script</div>
        <p className="text-sm text-slate-600 mb-3">
          Paste this before the{" "}
          <SysPill>&lt;/body&gt;</SysPill>{" "}
          tag on every page where you want the chat widget. Once it&apos;s on your site,
          all configuration — routing, surveys, chat window appearance — is managed from the{" "}
          <Link to="/rules" className="text-primary underline font-medium">Rules</Link> page.
          You should never need to touch the script again.
        </p>

        {/* If already deployed — backward compat callout */}
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
          <strong>Already deployed?</strong> Your existing script keeps working exactly as-is — no changes needed.
          The script below is what you would use for a fresh install.
        </div>

        <div className="relative">
          <textarea
            className="input w-full h-44 font-mono text-xs"
            readOnly
            value={embedScript}
          />
          <button
            type="button"
            className="absolute top-2 right-2 text-xs bg-white border border-slate-300 rounded px-2 py-1 hover:bg-slate-50"
            onClick={() => copy(embedScript, "embed")}
          >
            {copied === "embed" ? "Copied!" : "Copy"}
          </button>
        </div>
      </section>

      {/* ── 2. How routing works ── */}
      <details open className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white flex items-center justify-between">
          How routing works
          <span className="text-xs text-slate-400 font-normal ml-2">(click to collapse)</span>
        </summary>
        <div className="px-4 pb-4 pt-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
            <ol className="text-blue-700 space-y-2 list-decimal list-inside">
              <li>Visitor arrives — deployment is identified from the embed script.</li>
              <li>
                Rules are evaluated in priority order (lowest number = highest
                priority). The <strong>first matching rule</strong> wins and
                determines the team, prechat survey, workflow, etc.
              </li>
              <li>
                If <em>no rule matches</em>, the chat is sent to the{" "}
                <strong>default team</strong> you set on the General tab.
              </li>
            </ol>
            <p className="text-blue-600 text-xs pt-1 border-t border-blue-200">
              Add <strong>Route to Team</strong> rules in the <Link to="/rules" className="text-primary underline font-medium">Rules</Link> page to route
              conditionally — by language, URL, visitor data, time, or any
              combination — all managed from the admin dashboard.
            </p>
          </div>
        </div>
      </details>

      {/* ── 3. Data available automatically ── */}
      <section>
        <div className="font-medium text-sm text-slate-800 mb-1">
          Data available automatically — no extra code needed
        </div>
        <p className="text-sm text-slate-600 mb-3">
          These values are captured the moment a visitor opens the chat. Use
          them directly as conditions in rules or as variables in workflows.
        </p>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
            <span>What&apos;s captured</span>
            <span>Use as this variable / condition</span>
            <span>Example value</span>
          </div>
          {[
            { what: "Browser language",        variable: "<<language>>",  example: "en",           asCondition: false },
            { what: "Page URL",                variable: "URL condition", example: "/support",      asCondition: true },
            { what: "Domain",                  variable: "Domain condition", example: "shop.co", asCondition: true },
            { what: "Visitor country (by IP)", variable: "Country condition", example: "US",      asCondition: true },
            { what: "Channel",                 variable: "Channel condition", example: "web",     asCondition: true },
          ].map(({ what, variable, example, asCondition }, i) => (
            <div
              key={i}
              className={`grid grid-cols-3 px-4 py-2.5 items-center text-sm ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
            >
              <span className="text-slate-600">{what}</span>
              {asCondition ? (
                <span className="text-slate-500 text-xs italic">{variable}</span>
              ) : (
                <VarPill name={variable.replace(/<<|>>/g, "")} />
              )}
              <ValPill value={example} />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          <strong>Language</strong> is also available as the{" "}
          <VarPill name="language" /> variable inside workflow messages and
          Set Data nodes — e.g. send a greeting in the visitor&apos;s own language.
        </p>
      </section>

      {/* ── 3b. Language routing ── */}
      <details open className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white">
          Language routing — the easiest routing you can add
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
            <span className="font-semibold text-green-800">No code needed.</span>
            <span className="text-green-700 ml-1">
              The browser language is captured automatically. Just create a rule in the <Link to="/rules" className="text-primary underline font-medium">Rules</Link> page.
            </span>
          </div>

          <div className="text-sm text-slate-600">
            <strong>Example:</strong> Route Spanish speakers to your Spanish support team.
            <ol className="list-decimal list-inside mt-2 space-y-1 text-slate-600">
              <li>Rules page → <strong>Add Rule</strong> → Route to Team</li>
              <li>Add condition: <strong>Language</strong> equals <ValPill value="es" /></li>
              <li>Set team: <em>Spanish Support</em></li>
              <li>Priority: <em>1</em> (fires before the default catch-all)</li>
            </ol>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-4 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <span>Detection method</span>
              <span>Priority</span>
              <span>Example</span>
              <span>Code needed?</span>
            </div>
            {[
              { method: "JavaScript override",    priority: "1st (highest)", example: 'visitor.language = "es"',                    code: "Yes — Tier 3" },
              { method: "Meta tag velaro:lang",   priority: "2nd",           example: '<meta name="velaro:lang" content="es" />',   code: "Yes — Tier 2" },
              { method: "HTML lang attribute",    priority: "3rd",           example: '<html lang="es">',                           code: "No" },
              { method: "Browser navigator",      priority: "4th (default)", example: 'navigator.language → "es-MX"',               code: "No" },
            ].map(({ method, priority, example, code }, i) => (
              <div key={i} className={`grid grid-cols-4 px-4 py-2.5 items-start text-xs [&>*]:min-w-0 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <span className="text-slate-700 font-medium">{method}</span>
                <span className="text-slate-500">{priority}</span>
                <SysPill>{example}</SysPill>
                <span className={code === "No" ? "text-green-600 font-medium" : "text-amber-600"}>{code}</span>
              </div>
            ))}
          </div>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              Override language via JavaScript (e.g. from user preferences)
            </summary>
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mt-2 mb-3">
                Use this when your app stores language preference in a database or user settings —
                you want to use <em>that</em> instead of the browser&apos;s setting.
              </p>
              <CopyBlock id="lang-override" copied={copied} onCopy={copy} text={LANG_OVERRIDE_SNIPPET} />
            </div>
          </details>
        </div>
      </details>

      {/* ── 3c. Channel routing ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white">
          Channel routing (web, SMS, Facebook, WhatsApp)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <p className="text-sm text-slate-600">
            The <strong>Channel</strong> condition lets you route differently depending on where
            the visitor is chatting from — no code required. The channel is detected automatically
            from how the deployment was embedded or opened.
          </p>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <span>Channel value in rule</span>
              <span>When this fires</span>
              <span>Typical use</span>
            </div>
            {[
              { val: "web",       when: "Standard chat widget on a web page",           use: "Default — most common" },
              { val: "sms",       when: "Visitor texted your SMS number",               use: "Route SMS to phone-trained team" },
              { val: "facebook",  when: "Visitor messaged via Facebook Messenger",       use: "Social media team" },
              { val: "whatsapp",  when: "Visitor messaged via WhatsApp",                use: "International / mobile-first team" },
            ].map(({ val, when, use }, i) => (
              <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-start text-xs ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <ValPill value={val} />
                <span className="text-slate-600">{when}</span>
                <span className="text-slate-500 italic">{use}</span>
              </div>
            ))}
          </div>

          <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 border border-slate-200">
            <strong>To set up:</strong> Rules page → Add Rule → Route to Team → Add condition: <strong>Channel</strong> equals <ValPill value="sms" /> → Select your SMS team.
          </div>
        </div>
      </details>

      {/* ── 4. Passing data from the customer's page ── */}
      <section>
        <div className="font-medium text-sm text-slate-800 mb-1">
          Passing data from your website into the chat
        </div>
        <p className="text-sm text-slate-600 mb-4">
          If your website knows something about the visitor — they&apos;re logged
          in, you can read their account tier from the page, or you want to
          send the page&apos;s language preference — you can pass that into the
          chat so it becomes a variable you can use in rules and workflows.
        </p>

        {/* Tier summary */}
        <div className="grid gap-3 md:grid-cols-3 mb-6">
          {[
            {
              tier: "Tier 1",
              label: "No code needed",
              color: "green",
              items: ["Browser language", "Page URL & domain", "Country (IP)", "Channel"]
            },
            {
              tier: "Tier 2",
              label: "Add meta tags to your HTML",
              color: "blue",
              items: ["Any static value your server knows", "No JavaScript required", "Just add <meta> tags"]
            },
            {
              tier: "Tier 3",
              label: "Inject via JavaScript",
              color: "amber",
              items: ["Logged-in user data", "Dynamic values", "Data from your app or CRM"]
            }
          ].map(({ tier, label, color, items }) => (
            <div key={tier} className={`rounded-lg border p-3 bg-${color}-50 border-${color}-200`}>
              <div className={`text-xs font-semibold text-${color}-700 mb-0.5`}>{tier}</div>
              <div className={`text-sm font-medium text-${color}-800 mb-2`}>{label}</div>
              <ul className={`text-xs text-${color}-700 space-y-1 list-disc list-inside`}>
                {items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>

        {/* Tier 2: Meta tag convention */}
        <div className="mb-4">
          <div className="text-sm font-medium text-slate-700 mb-1">
            Tier 2 — Meta tag convention (no JavaScript)
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Add meta tags with the prefix{" "}
            <SysPill>velaro:</SysPill> to your HTML{" "}
            <SysPill>&lt;head&gt;</SysPill>. The chat widget reads these
            automatically. The part after <SysPill>velaro:</SysPill> becomes
            the variable name — it must match a{" "}
            <Link to="/contact_fields" className="text-blue-600 hover:underline">
              Custom Field
            </Link>{" "}
            you&apos;ve defined.
          </p>

          {/* INPUT → VARIABLE → RULE table */}
          <div className="rounded-lg border border-slate-200 overflow-hidden mb-3">
            <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <span>You add to your page</span>
              <span>Becomes variable</span>
              <span>Use in a rule condition as</span>
            </div>
            {[
              { tag: 'velaro:plan" content="enterprise', varName: "plan",        ruleVal: "enterprise" },
              { tag: 'velaro:customer_id" content="4821', varName: "customerId", ruleVal: "4821" },
              { tag: 'velaro:lang" content="es',          varName: "lang",       ruleVal: "es" },
            ].map(({ tag, varName, ruleVal }, i) => (
              <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-center text-xs [&>*]:min-w-0 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <SysPill>{`<meta name="${tag}" />`}</SysPill>
                <VarPill name={varName} />
                <span>
                  Attribute <VarPill name={varName} /> equals <ValPill value={ruleVal} />
                </span>
              </div>
            ))}
          </div>

          <CopyBlock id="meta" copied={copied} onCopy={copy} text={META_TAG_SNIPPET} />
        </div>

        {/* Tier 3: JavaScript */}
        <div className="mb-4">
          <div className="text-sm font-medium text-slate-700 mb-1">
            Tier 3 — Inject via JavaScript
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Set{" "}
            <SysPill>window.__velaro.visitor</SysPill>{" "}
            <strong>before</strong> the embed script. Property names must match
            your{" "}
            <Link to="/contact_fields" className="text-blue-600 hover:underline">
              Custom Field variable names
            </Link>
            . Each property value becomes the{" "}
            <VarPill name="fieldName" /> variable available in workflows and
            Attribute conditions in rules.
          </p>

          {/* INPUT → VARIABLE → RULE table */}
          <div className="rounded-lg border border-slate-200 overflow-hidden mb-3">
            <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <span>You set in JavaScript</span>
              <span>Becomes variable</span>
              <span>Use in a rule condition as</span>
            </div>
            {[
              { js: "visitor.plan = \"enterprise\"",   varName: "plan",  ruleVal: "enterprise" },
              { js: "visitor.email = \"a@co.com\"",    varName: "email", ruleVal: "a@co.com" },
              { js: "visitor.name = \"Jane Smith\"",   varName: "name",  ruleVal: "Jane Smith" },
            ].map(({ js, varName, ruleVal }, i) => (
              <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-center text-xs [&>*]:min-w-0 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <SysPill>{js}</SysPill>
                <VarPill name={varName} />
                <span>
                  Attribute <VarPill name={varName} /> equals <ValPill value={ruleVal} />
                </span>
              </div>
            ))}
          </div>

          <CopyBlock id="visitor" copied={copied} onCopy={copy} text={visitorSnippet} />
        </div>

        {/* Expandable source patterns */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
            Where to read values from (JavaScript patterns)
          </div>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              From a server-rendered page (logged-in user data)
            </summary>
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mt-2 mb-3">
                Your server template engine renders user data into the page.
                Replace <SysPill>{"{{user.field}}"}</SysPill> with your actual
                template syntax (Blade, Jinja, Razor, etc.).
              </p>
              <CopyBlock id="server" copied={copied} onCopy={copy} text={SERVER_SNIPPET} />
            </div>
          </details>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              From a URL query parameter
            </summary>
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mt-2 mb-2">
                Example: visitor comes from{" "}
                <SysPill>yoursite.com/support?source=pricing</SysPill> — pass
                the source so you can route or greet them accordingly.
                Result: <VarPill name="source" /> ={" "}
                <ValPill value="pricing" />
              </p>
              <CopyBlock id="url" copied={copied} onCopy={copy} text={URL_PARAM_SNIPPET} />
            </div>
          </details>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              From a cookie
            </summary>
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mt-2 mb-2">
                Useful when a visitor was identified in a previous session
                and your site sets a cookie. Only works for non-HttpOnly cookies.
              </p>
              <CopyBlock id="cookie" copied={copied} onCopy={copy} text={COOKIE_SNIPPET} />
            </div>
          </details>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
              From the page&apos;s HTML lang attribute (override browser language)
            </summary>
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mt-2 mb-2">
                If your page sets{" "}
                <SysPill>{"<html lang=\"es\">"}</SysPill> and you want to use
                that instead of (or in addition to) the browser setting, read it
                directly. Result: <VarPill name="pageLang" /> ={" "}
                <ValPill value="es" />
              </p>
              <CopyBlock id="lang" copied={copied} onCopy={copy} text={PAGE_LANG_SNIPPET} />
            </div>
          </details>
        </div>
      </section>

      {/* ── 4b. In-chat answers → workflow branching ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white">
          In-chat answers driving workflow branching (Ask Question → Branch)
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-3">
          <p className="text-sm text-slate-600">
            Use an <strong>Ask Question</strong> node in a workflow to collect a visitor&apos;s response,
            save it as a variable, then use a <strong>Branch</strong> node to route differently based
            on what they said.
          </p>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <span>Workflow step</span>
              <span>Configuration</span>
              <span>Result</span>
            </div>
            {[
              { step: "Ask Question node",  config: "Save Reply As → Conversation Variable → \"department\"",    result: <>Visitor&apos;s answer stored in <VarPill name="department" /></> },
              { step: "Branch node",        config: "Condition: Attribute department equals \"billing\"",         result: "If yes → Billing path; otherwise → General path" },
              { step: "Route to Team node", config: "Team: Billing Team",                                        result: "Chat routed to billing specialists" },
            ].map(({ step, config, result }, i) => (
              <div key={i} className={`grid grid-cols-3 px-4 py-2.5 items-start text-xs [&>*]:min-w-0 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <span className="text-slate-700 font-medium">{step}</span>
                <SysPill>{config}</SysPill>
                <span className="text-slate-600">{result}</span>
              </div>
            ))}
          </div>

          <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <strong className="text-blue-700">Tip:</strong> Use <strong>Conversation Variable</strong> scope for answers that only matter in this chat (like &quot;which department?&quot;), and <strong>Contact Variable</strong> if you want to remember it for future conversations (like &quot;preferred language&quot;).
          </div>
        </div>
      </details>

      {/* ── 4c. Passing context to AI bot ── */}
      <details className="border border-slate-200 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-50 select-none bg-white">
          Passing visitor context to your AI bot
        </summary>
        <div className="px-4 pb-4 pt-2 space-y-4">
          <p className="text-sm text-slate-600">
            When a visitor starts a chat and your bot engages, the bot can access any
            variables you&apos;ve set — from the embed script, from prechat survey answers,
            or from prior workflow steps. This lets the bot personalize responses
            without asking the visitor to repeat themselves.
          </p>

          {[
            {
              title: "Personalized greeting",
              desc: <>Visitor data passed via JS sets <VarPill name="name" /> and <VarPill name="plan" />. Bot message: <em>&quot;Hi {"<<name>>"}, I can see you&apos;re on the {"<<plan>>"} plan — what can I help with?&quot;</em></>,
              detail: "Set name and plan via window.__velaro.visitor before the embed script. Both are immediately available in the Send Message node or passed to the AI bot as system context."
            },
            {
              title: "Branch by plan tier",
              desc: <>Condition node checks <VarPill name="plan" /> equals <ValPill value="enterprise" /> → routes to dedicated enterprise bot flow with full CRM access.</>,
              detail: "Use a Branch node at the start of the workflow. One path for enterprise, one for standard. Each path can call a different HTTP Request node to pull CRM data."
            },
            {
              title: "CRM lookup before bot responds",
              desc: <>HTTP Request node calls your API with <VarPill name="customerId" />, saves result to <VarPill name="accountStatus" />. Bot uses <VarPill name="accountStatus" /> in its first message.</>,
              detail: "Chain: Set Data (read customerId) → HTTP Request (GET /api/customers/<<customerId>>) → Set Data (save response.status to accountStatus) → Send Message/AI node."
            },
            {
              title: "Language-aware bot",
              desc: <>Bot detects <VarPill name="language" /> and responds in the visitor&apos;s language automatically, or branches to language-specific workflows.</>,
              detail: "Branch node: Language equals \"es\" → Spanish workflow with Spanish-language bot messages. Language equals \"fr\" → French workflow. Default → English."
            }
          ].map(({ title, desc, detail }, i) => (
            <details key={i} className="border border-slate-200 rounded-lg">
              <summary className="px-4 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">{i + 1}</span>
                {title}
              </summary>
              <div className="px-4 pb-4 pt-2 space-y-2">
                <div className="text-sm text-slate-600">{desc}</div>
                <div className="text-xs text-slate-500 bg-slate-50 rounded p-3 border border-slate-100">{detail}</div>
              </div>
            </details>
          ))}

          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">
              Starter snippet — set all context before the embed script
            </div>
            <CopyBlock id="bot-context" copied={copied} onCopy={copy} text={BOT_CONTEXT_SNIPPET} />
          </div>
        </div>
      </details>

      {/* ── 5. Complete setup flow ── */}
      <section>
        <div className="font-medium text-sm text-slate-800 mb-3">
          Complete setup: from page data to routing rule
        </div>
        <div className="space-y-1">
          {[
            {
              n: 1, color: "blue",
              title: "Identify the data you have",
              desc: "What does your page know? Browser language (free), a meta tag, a cookie, or a server-rendered value?"
            },
            {
              n: 2, color: "blue",
              title: "Create a Custom Field",
              desc: <>
                Go to <Link to="/contact_fields" className="text-blue-600 hover:underline">Custom Fields</Link> and
                add a field. Set its scope (Contact = saved across chats,
                Conversation = this chat only). The <strong>variable name</strong> you
                give it is what you&apos;ll use everywhere.
              </>
            },
            {
              n: 3, color: "amber",
              title: "Pass the data to the chat",
              desc: <>
                Use Tier 2 (meta tag) or Tier 3 (JavaScript) above to set the
                value. Property name must exactly match the variable name from
                step 2. Now <VarPill name="yourField" /> is available in
                rules and workflows.
              </>
            },
            {
              n: 4, color: "amber",
              title: "Build a rule",
              desc: <>
                Go to the <Link to="/rules" className="text-primary underline font-medium">Rules</Link> page. Create a{" "}
                <strong>Route to Team</strong> rule. Add an{" "}
                <strong>Attribute</strong> condition: field{" "}
                <VarPill name="yourField" /> equals{" "}
                <ValPill value="your value" />. Set priority to 1 so it fires
                before the default.
              </>
            },
            {
              n: 5, color: "green",
              title: "Test it",
              desc: "Open the chat on your page with the data set. The rule fires, chat routes to the correct team automatically."
            }
          ].map(({ n, color, title, desc }) => (
            <div key={n} className="flex gap-3 items-start">
              <div className={`shrink-0 w-6 h-6 rounded-full bg-${color}-100 border border-${color}-300 flex items-center justify-center text-xs font-bold text-${color}-700 mt-0.5`}>
                {n}
              </div>
              <div className="flex-1 pb-3 border-b border-slate-100 last:border-0">
                <div className="text-sm font-medium text-slate-800">{title}</div>
                <div className="text-sm text-slate-600 mt-0.5">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 6. Security — CSP, SRI, Trusted Types ── */}
      <section>
        <div className="font-medium text-sm text-slate-800 mb-1">
          Security: Content Security Policy &amp; more
        </div>
        <p className="text-sm text-slate-600 mb-3">
          If your website enforces a Content Security Policy (CSP), use the guides
          below to configure it for the Velaro widget. If you don't use CSP, no
          changes are needed — the standard embed code works as-is.
        </p>
        <DeploymentSecurityGuide embedScript={embedScript} variant="messenger" />
      </section>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Code snippet constants
// ---------------------------------------------------------------------------

const META_TAG_SNIPPET = `<!-- Add to your page's <head> — no JavaScript needed -->
<!-- Names after "velaro:" become variable names in the chat -->

<meta name="velaro:plan"        content="enterprise" />
<meta name="velaro:customer_id" content="4821" />

<!-- Then paste the embed script as usual -->`;

const SERVER_SNIPPET = `<script>
  // Server renders user values directly into the page
  // Replace {{user.field}} with your template engine's syntax
  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    name:       "{{user.fullName}}",      // <<name>> in workflows
    email:      "{{user.email}}",         // <<email>> in workflows
    plan:       "{{user.accountTier}}",   // <<plan>> in rules / workflows
    customerId: "{{user.id}}"             // <<customerId>> in rules / workflows
  };
</script>
<!-- paste embed script below -->`;

const URL_PARAM_SNIPPET = `<script>
  // Read a URL query parameter and pass it to the chat
  // e.g. yoursite.com/support?source=pricing  →  <<source>> = "pricing"
  const params = new URLSearchParams(window.location.search);
  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    source: params.get("source") || undefined   // <<source>> in rules / workflows
  };
</script>
<!-- paste embed script below -->`;

const COOKIE_SNIPPET = `<script>
  // Helper to read a cookie by name
  function getCookie(name) {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    plan:       getCookie("user_plan"),      // <<plan>> in rules / workflows
    customerId: getCookie("user_id")         // <<customerId>> in rules / workflows
  };
</script>
<!-- paste embed script below -->`;

const PAGE_LANG_SNIPPET = `<script>
  // Read the page's HTML lang attribute instead of (or alongside) browser language
  // e.g. <html lang="es">  →  <<pageLang>> = "es"
  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    pageLang: document.documentElement.lang || undefined
  };
</script>
<!-- paste embed script below -->`;

const LANG_OVERRIDE_SNIPPET = `<script>
  // Use your app's stored language preference instead of the browser setting
  // Replace userPrefs.language with however your app exposes it
  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    language: userPrefs.language   // e.g. "es", "fr", "pt-BR"
  };
</script>
<!-- paste embed script below -->`;

const BOT_CONTEXT_SNIPPET = `<script>
  // Set ALL visitor context BEFORE the embed script.
  // Property names match your Custom Field variable names.
  window.__velaro = window.__velaro || {};
  window.__velaro.visitor = {
    name:       "{{user.fullName}}",    // <<name>>       — personalize greeting
    email:      "{{user.email}}",       // <<email>>      — identify contact
    plan:       "{{user.tier}}",        // <<plan>>       — branch by plan
    language:   "{{user.language}}",   // <<language>>   — language-aware bot
    customerId: "{{user.id}}"          // <<customerId>> — CRM lookup
  };
</script>
<!-- paste embed script below -->`;

function buildVisitorSnippet(customFieldNames: string[]): string {
  const always = [
    { key: "name",  note: "<<name>> in workflows" },
    { key: "email", note: "<<email>> in workflows" }
  ];

  const custom = customFieldNames
    .filter((n) => n !== "name" && n !== "email")
    .slice(0, 6)
    .map((n) => `    ${n.padEnd(14)}: undefined   // <<${n}>> in rules / workflows`);

  const alwaysLines = always.map(
    ({ key, note }) => `    ${key.padEnd(14)}: undefined   // ${note}`
  );

  return [
    "<script>",
    "  window.__velaro = window.__velaro || {};",
    "  window.__velaro.visitor = {",
    ...alwaysLines,
    ...(custom.length > 0 ? ["", "    // Your custom fields:"] : []),
    ...custom,
    "  };",
    "</script>",
    "<!-- paste embed script below -->"
  ].join("\n");
}
